import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productService, categoryService, carModelService, carYearService, carBrandService } from '../../services/api';
import Loading from '../../components/Loading';

const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [categories, setCategories] = useState([]);
  const [carModels, setCarModels] = useState([]);
  const [carYears, setCarYears] = useState([]);
  const [carBrands, setCarBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    part_number: '',
    price: '',
    stock_quantity: '',
    category_id: '',
    brand: '',
    image_url: '',
    car_models: [],
    car_years: [],
    car_brands: []
  });

  useEffect(() => {
    fetchCategories();
    fetchCarModels();
    fetchCarYears();
    fetchCarBrands();
    if (isEdit) {
      fetchProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAll();
      setCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCarModels = async () => {
    try {
      const response = await carModelService.getAll();
      setCarModels(response.data.data);
    } catch (error) {
      console.error('Error fetching car models:', error);
    }
  };

  const fetchCarYears = async () => {
    try {
      const response = await carYearService.getAll();
      setCarYears(response.data.data);
    } catch (error) {
      console.error('Error fetching car years:', error);
    }
  };

  const fetchCarBrands = async () => {
    try {
      const response = await carBrandService.getAll();
      setCarBrands(response.data.data);
    } catch (error) {
      console.error('Error fetching car brands:', error);
    }
  };

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await productService.getById(id);
      const productData = response.data.data;
      
      // Parse JSON fields if they are strings
      if (typeof productData.car_models === 'string') {
        productData.car_models = JSON.parse(productData.car_models || '[]');
      }
      if (typeof productData.car_years === 'string') {
        productData.car_years = JSON.parse(productData.car_years || '[]');
      }
      if (typeof productData.car_brands === 'string') {
        productData.car_brands = JSON.parse(productData.car_brands || '[]');
      }
      
      setFormData(productData);
      if (productData.image_url) {
        setImagePreview(productData.image_url);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      alert('فشل تحميل المنتج');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCheckboxChange = (fieldName, itemId) => {
    const currentValues = formData[fieldName] || [];
    const numericId = parseInt(itemId);
    
    let updatedValues;
    if (currentValues.includes(numericId)) {
      // Remove if already selected
      updatedValues = currentValues.filter(id => id !== numericId);
    } else {
      // Add if not selected
      updatedValues = [...currentValues, numericId];
    }
    
    setFormData({
      ...formData,
      [fieldName]: updatedValues
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create local preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData({
      ...formData,
      image_url: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      let imageUrl = formData.image_url || '';
      
      // Upload image if a new file was selected
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);
        
        const API_URL = process.env.REACT_APP_API_URL || 'https://api.iraq-parts.com/api';
        const uploadResponse = await fetch(`${API_URL}/products/upload-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: imageFormData
        });
        
        const uploadData = await uploadResponse.json();
        if (uploadData.success) {
          imageUrl = uploadData.imageUrl;
        }
      }
      
      const productData = {
        ...formData,
        image_url: imageUrl,
        car_models: JSON.stringify(formData.car_models || []),
        car_years: JSON.stringify(formData.car_years || []),
        car_brands: JSON.stringify(formData.car_brands || [])
      };
      
      if (isEdit) {
        await productService.update(id, productData);
        alert('تم تحديث المنتج بنجاح');
      } else {
        await productService.create(productData);
        alert('تم إضافة المنتج بنجاح');
      }
      navigate('/admin/products');
    } catch (error) {
      console.error('Error saving product:', error);
      alert('فشل حفظ المنتج');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) return <Loading />;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8">
        {isEdit ? 'تعديل المنتج' : 'إضافة منتج جديد'}
      </h1>

      <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">اسم المنتج</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">الوصف</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">رقم القطعة</label>
              <input
                type="text"
                name="part_number"
                value={formData.part_number}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">العلامة التجارية</label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">موديلات السيارات</label>
            <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {carModels.map(model => (
                  <label key={model.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded">
                    <input
                      type="checkbox"
                      checked={(formData.car_models || []).includes(model.id)}
                      onChange={() => handleCheckboxChange('car_models', model.id)}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <span className="text-sm">{model.brand_name} - {model.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-1">حدد الموديلات المتوافقة</p>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">سنوات الصنع</label>
              <div className="border border-gray-300 rounded-lg p-4 max-h-48 overflow-y-auto bg-gray-50">
                <div className="grid grid-cols-2 gap-2">
                  {carYears.map(year => (
                    <label key={year.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded">
                      <input
                        type="checkbox"
                        checked={(formData.car_years || []).includes(year.id)}
                        onChange={() => handleCheckboxChange('car_years', year.id)}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="text-sm">{year.year}</span>
                    </label>
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">حدد السنوات المتوافقة</p>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">العلامات التجارية للسيارات</label>
              <div className="border border-gray-300 rounded-lg p-4 max-h-48 overflow-y-auto bg-gray-50">
                <div className="grid grid-cols-1 gap-2">
                  {carBrands.map(brand => (
                    <label key={brand.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded">
                      <input
                        type="checkbox"
                        checked={(formData.car_brands || []).includes(brand.id)}
                        onChange={() => handleCheckboxChange('car_brands', brand.id)}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="text-sm">{brand.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-1">حدد العلامات المتوافقة</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 font-semibold mb-2">السعر ($)</label>
              <input
                type="number"
                step="0.01"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">الكمية</label>
              <input
                type="number"
                name="stock_quantity"
                value={formData.stock_quantity}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 font-semibold mb-2">القسم</label>
            <select
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">اختر القسم</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">صورة المنتج</label>
            
ProductManagement.js:237  POST http://localhost:3000/api/products/upload-image net::ERR_CONNECTION_ABORTED            {/* Image Button Upload */}
            <div className="flex flex-col items-start gap-4">
              <label className="cursor-pointer group relative">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <div className={`w-48 h-48 rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-all ${
                  imagePreview 
                    ? 'border-gray-300' 
                    : 'border-gray-400 hover:border-blue-500 hover:bg-blue-50'
                }`}>
                  {imagePreview ? (
                    <>
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white">
                        <svg className="w-12 h-12 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="font-semibold">تغيير الصورة</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <svg className="w-12 h-12 text-gray-400 group-hover:text-blue-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-gray-600 font-semibold group-hover:text-blue-500">اضغط لاختيار صورة</span>
                      <span className="text-gray-400 text-sm mt-1">PNG, JPG, GIF حتى 10MB</span>
                    </>
                  )}
                </div>
              </label>

              {imagePreview && (
                <button
                  type="button"
                  onClick={removeImage}
                  className="flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  إزالة الصورة
                </button>
              )}
            </div>

            {/* Or use URL */}
            <div className="mt-4">
              <label className="block text-gray-600 text-sm mb-1">أو استخدم رابط صورة</label>
              <input
                type="url"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'جاري الحفظ...' : (isEdit ? 'تحديث' : 'إضافة')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="flex-1 bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-400"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProductForm;
